import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Box, { BoxProps as BoxProps } from '@mui/material/Box';
import { Divider } from '@classity-edtech/ui-base/dist/components/Displays';
import { styled } from '@mui/material/styles';
import { Icons, useLocalStorage } from '@classity-edtech/ui-base';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { useNavigate } from 'react-router-dom';
import { Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { webPaths } from '../../routes/paths';
import useCurrentUser from '../../hooks/useCurrentUser';
import { FormattedMessage } from 'react-intl';

export interface DashboardNavProps extends BoxProps {
  toggleSideNav: () => void;
}

export const ProfileDropdown = ({ className, toggleSideNav, ...props }: DashboardNavProps) => {
  const navigate = useNavigate();
  const [, setUserFlow] = useLocalStorage('userFlow', '');
  const [showProfile, setShowProfile] = useState(false);

  const user = useCurrentUser();

  const toggleShowProfile = () => {
    setShowProfile(showProfile => !showProfile);
  };

  const handleRegisterChild = () => {
    setUserFlow('assessment');
    navigate(`/${webPaths.onboardStudent}`);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate(`/`);
  };

  const handleSigninNavigation = () => {
    navigate(`/${webPaths.signin}`);
  };

  const localisedMessage = (id: string) => {
    const F: any = FormattedMessage;
    return <F id={id} />;
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{ display: 'flex', gap: 1, alignItems: 'center', cursor: 'pointer' }}
        onClick={toggleShowProfile}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            aspectRatio: '1 / 1',
            width: '36px',
            background: globalColours['$global-colour-background-overlay'],
            borderRadius: '100%',
          }}
        >
          <Icons.User
            sx={{
              color: globalColours['$global-colour-interactive-control-active'],
              width: '20px',
              height: '20px',
            }}
          />
        </Box>
        <Typography
          variant='body3'
          sx={{
            maxWidth: { xs: '64px', md: '80px' },
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {user?.user?.firstname}
        </Typography>
        <Icons.ArrowDownIcon
          sx={{
            transition: 'all 0.2s',
            width: '12px',
            height: '12px',
            color: globalColours['$global-colour-interactive-primary-default'],
            rotate: showProfile ? '180deg' : '0deg',
          }}
        />
      </Box>
      {showProfile && (
        <>
          <Box
            sx={{
              py: 4,
              px: 2,
              boxShadow: '0px 20px 66px 0px rgba(34, 48, 73, 0.20)',
              background: globalColours['$global-colour-background-elevated'],
              borderRadius: '24px',
              position: 'absolute',
              width: 'max-content',
              right: 0,
              top: 'calc(100% + 8px)',
              zIndex: 20,
            }}
          >
            <Typography variant='body3' sx={{ mb: 3, px: 2, pb: 8 }}>
              {user?.user?.userId ? (
                <>
                  {localisedMessage('dashboard.home.membership.number')} P
                  {user?.user?.ids?.find(id => id.name === 'uid').value}
                </>
              ) : (
                <>{localisedMessage('guest')}</>
              )}
            </Typography>
            <Box
              sx={{
                py: 1.5,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                cursor: 'pointer',
              }}
              onClick={user?.user?.userId ? handleLogout : handleSigninNavigation}
            >
              <Icons.Logout
                sx={{
                  width: '20px',
                  height: '20px',
                  color: globalColours['$global-colour-content-secondary'],
                }}
              />
              {user?.user?.userId ? (
                <Typography variant='body3'>{localisedMessage('dashboard.home.logout')}</Typography>
              ) : (
                <Typography variant='body3'>{localisedMessage('auth.signin')}</Typography>
              )}
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                cursor: 'pointer',
              }}
              onClick={handleRegisterChild}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  aspectRatio: '1 / 1',
                  width: '36px',
                  background: globalColours['$global-colour-background-overlay'],
                  borderRadius: '100%',
                }}
              >
                <Add
                  sx={{
                    color: globalColours['$global-colour-interactive-secondary-hover'],
                    width: '20px',
                    height: '20px',
                  }}
                />
              </Box>
              <Typography variant='body3SemiBold'>
                {localisedMessage('dashboard.home.register.child')}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{ position: 'fixed', inset: 0, zIndex: 10, background: 'transparent' }}
            onClick={toggleShowProfile}
          ></Box>
        </>
      )}
    </Box>
  );
};

ProfileDropdown.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

ProfileDropdown.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(ProfileDropdown)(
  ({ theme }) => `
  &.MuiBox-root {
  }
`,
);
